import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Horizontal scroll testimonials — "Ils ont fait le voyage"
 * Card: name, city flow, pathway+year, italic quote, cedar-leaf rating.
 */

const CedarLeaf = () => (
  <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 inline-block" fill="none">
    <path
      d="M 8 1 L 12 8 L 8 15 L 4 8 Z"
      fill="#E8A045"
    />
    <line x1="8" y1="1" x2="8" y2="15" stroke="#0D1117" strokeWidth="0.6" />
  </svg>
);

const Testimonial = () => {
  const { t } = useLang();
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector("article")?.offsetWidth || 400;
    el.scrollBy({ left: dir * (cardWidth + 32), behavior: "smooth" });
  };

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-[#FAF8F5] text-[#0D1117] py-24 sm:py-32 overflow-hidden"
    >
      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 mb-12 sm:mb-16">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <span className="brushstroke mb-6" />
            <div className="font-mono-accent text-[10px] text-[#E8A045] mb-5">
              {t.testimonials.eyebrow}
            </div>
            <h2
              data-testid="testimonials-headline"
              className="font-display text-5xl sm:text-6xl lg:text-[5rem] tracking-tight leading-[1.02] text-[#0D1117] max-w-3xl"
            >
              {t.testimonials.headline}
            </h2>
          </div>

          {/* Scroll arrows */}
          <div className="flex items-center gap-3">
            <button
              data-testid="stories-prev"
              onClick={() => scroll(-1)}
              className="w-12 h-12 rounded-full border border-[#0D1117]/15 hover:border-[#E8A045] flex items-center justify-center text-[#0D1117] transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              data-testid="stories-next"
              onClick={() => scroll(1)}
              className="w-12 h-12 rounded-full border border-[#0D1117]/15 hover:border-[#E8A045] flex items-center justify-center text-[#0D1117] transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal scroll lane */}
      <div
        ref={scrollRef}
        className="hide-scrollbar relative flex gap-6 sm:gap-8 px-6 lg:px-10 overflow-x-auto pb-8 snap-x snap-mandatory"
        dir="ltr"
      >
        {t.testimonials.list.map((s, i) => (
          <motion.article
            key={i}
            data-testid={`testimonial-card-${i}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="flex-shrink-0 w-[88vw] sm:w-[440px] md:w-[480px] snap-start flex flex-col py-3"
          >
            <div className="flex items-center gap-1 mb-5">
              {[0, 1, 2, 3, 4].map((k) => (
                <CedarLeaf key={k} />
              ))}
            </div>
            <blockquote className="font-display italic text-xl sm:text-2xl leading-snug text-[#0D1117]/85 mb-8 flex-1">
              &ldquo;{s.quote}&rdquo;
            </blockquote>
            <div className="border-t border-[#0D1117]/15 pt-5">
              <div className="font-display text-lg text-[#0D1117] tracking-tight">
                {s.name}
              </div>
              <div className="font-mono-accent text-[10px] text-[#0D1117]/60 mt-1.5">
                {s.city}
              </div>
              <div className="font-mono-accent text-[10px] text-[#E8A045] mt-2">
                {s.pathway}
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
};

export default Testimonial;
